/**
 * Stepping through the results of a list from an entity's detail view (#109).
 *
 * The list the user came from is carried in the detail URL as `?from=`, the
 * list's own query string (search, quick filters, column filters), so a reload
 * or a shared link keeps the context. The grid's exact order (its sort and
 * column conditions) cannot be rebuilt outside MUI, so the list also passes its
 * visible ids in router state; history state survives a reload, a pasted link
 * does not carry it and falls back to search + quick filters only.
 */

import { useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { api } from '../../api'
import { PAGE_LIMIT } from '../../api'
import { applyQuick, readQuick } from './quickFilter'
import { filterRows, readSearch } from './rowFilter'

export const FROM_KEY = 'from'

export interface ResultState {
  resultIds?: string[]
}

/** The detail path for `id`, remembering the list query it was opened from. */
export function detailPath(plural: string, id: string, from: string): string {
  const base = `/${plural}/${encodeURIComponent(id)}`
  return from ? `${base}?${FROM_KEY}=${encodeURIComponent(from)}` : base
}

export interface ResultNav {
  /** Position in the results, 0-based, or -1 when this entity is not in them. */
  index: number
  total: number
  previous: string | null
  next: string | null
  /** Router state to pass along when stepping. */
  state?: ResultState
}

/** Pure part: the neighbours of `id` in `ids`. */
export function neighbours(ids: string[], id: string, plural: string, from: string): ResultNav | null {
  const index = ids.indexOf(id)
  if (index < 0 || ids.length < 2) return null
  return {
    index,
    total: ids.length,
    previous: index > 0 ? detailPath(plural, ids[index - 1], from) : null,
    next: index < ids.length - 1 ? detailPath(plural, ids[index + 1], from) : null,
  }
}

/**
 * The step controls' data, or null when the detail view was opened without a
 * list (then nothing is shown and the page behaves as before).
 */
export function useResultNav(plural: string, id: string): ResultNav | null {
  const [params] = useSearchParams()
  const location = useLocation()
  const from = params.get(FROM_KEY) ?? ''
  const stated = (location.state as ResultState | null)?.resultIds
  const [derived, setDerived] = useState<string[] | null>(null)

  useEffect(() => {
    setDerived(null)
    if (stated || !from || plural !== 'requirements') return undefined
    let live = true
    api
      .requirements({ limit: PAGE_LIMIT })
      .then((page) => {
        if (!live) return
        const list = new URLSearchParams(from)
        const rows = applyQuick(filterRows(page.items, readSearch(list)), readQuick(list))
        setDerived(rows.map((row) => String(row.id)))
      })
      .catch(() => live && setDerived(null))
    return () => {
      live = false
    }
  }, [from, plural, stated])

  const ids = stated ?? derived
  if (!ids) return null
  const found = neighbours(ids, id, plural, from)
  // The state is handed on only when the list supplied it; a derived list is
  // re-derived from the URL at the next entity.
  return found && { ...found, state: stated ? { resultIds: stated } : undefined }
}
